import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import EQDialog from './EQDialog';
import GateDialog from './GateDialog';
import CompressorDialog from './CompressorDialog';
import MasterEQDialog from './MasterEQDialog';
import MasterCompressorDialog from './MasterCompressorDialog';

// Memoized vertical slider overlay component to avoid parent-triggered re-renders
export const VerticalSliderOverlay = React.memo(function VerticalSliderOverlay({ value = 0, onChange, onDoubleClick, maxLinear = 1 }) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const draggingRef = useRef(false);
  const docMoveRef = useRef(null);
  const docUpRef = useRef(null);
  const [topPercent, setTopPercent] = useState(50);
  const pendingRef = useRef(null);
  const [localNorm, setLocalNorm] = useState(() => {
    const v = isNaN(value) ? 0 : value;
    if ((typeof maxLinear !== 'number') || maxLinear <= 1) return Math.max(0, Math.min(1, v / Math.max(1, maxLinear)));
    if (v <= 1) return 0.5 * (v / 1);
    return 0.5 + 0.5 * ((v - 1) / (maxLinear - 1));
  });

  useEffect(() => {
    if (draggingRef.current) return;
    const v = isNaN(value) ? 0 : value;
    let norm;
    if ((typeof maxLinear !== 'number') || maxLinear <= 1) norm = Math.max(0, Math.min(1, v / Math.max(1, maxLinear)));
    else {
      if (v <= 1) norm = 0.5 * (v / 1);
      else norm = 0.5 + 0.5 * ((v - 1) / (maxLinear - 1));
    }
    setLocalNorm(norm);
  }, [value, maxLinear]);

  useEffect(() => {
    const norm = isNaN(localNorm) ? 0 : Math.max(0, Math.min(1, localNorm));
    const topPct = (1 - norm) * 100;
    setTopPercent(topPct);
  }, [localNorm]);

  function linearToNorm(v) {
    if ((typeof maxLinear !== 'number') || maxLinear <= 1) return Math.max(0, Math.min(1, v / Math.max(1, maxLinear)));
    if (v <= 1) return 0.5 * (v / 1);
    return 0.5 + 0.5 * ((v - 1) / (maxLinear - 1));
  }

  const zeroNorm = linearToNorm(1);
  const zeroTopPct = (1 - zeroNorm) * 100;

  function normToLinear(norm) {
    if ((typeof maxLinear !== 'number') || maxLinear <= 1) return norm * Math.max(1, maxLinear);
    if (norm <= 0.5) return (norm / 0.5) * 1;
    return 1 + ((norm - 0.5) / 0.5) * (maxLinear - 1);
  }

  function scheduleCommit(norm) {
    const linear = normToLinear(norm);
    if (draggingRef.current) {
      pendingRef.current = linear;
      return;
    }
    try { if (typeof onChange === 'function') onChange(linear); } catch (err) {}
  }

  const handleWrapperPointerDown = (e) => {
    const ev = e;
    const el = inputRef.current || wrapRef.current;
    if (el && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const thumbH = 20;
      const innerTop = rect.top + thumbH / 2;
      const innerHeight = Math.max(1, rect.height - thumbH);
      let ratio = 1 - ((ev.clientY - innerTop) / innerHeight);
      ratio = Math.max(0, Math.min(1, ratio));
      setLocalNorm(ratio);
      scheduleCommit(ratio);
    }
    draggingRef.current = true;
    try { if (wrapRef.current.setPointerCapture) wrapRef.current.setPointerCapture(e.pointerId); } catch (err) {}

    const docMove = (ev2) => {
      try {
        const el2 = inputRef.current || wrapRef.current;
        if (!el2) return;
        const rect2 = inputRef.current.getBoundingClientRect();
        const thumbH2 = 20;
        const innerTop2 = rect2.top + thumbH2 / 2;
        const innerHeight2 = Math.max(1, rect2.height - thumbH2);
        let ratio2 = 1 - ((ev2.clientY - innerTop2) / innerHeight2);
        ratio2 = Math.max(0, Math.min(1, ratio2));
        setLocalNorm(ratio2);
        scheduleCommit(ratio2);
      } catch (err) {}
      try { ev2.preventDefault(); } catch (err) {}
    };

    const docUp = (ev2) => {
      draggingRef.current = false;
      try { if (wrapRef.current.releasePointerCapture && typeof ev2.pointerId === 'number') wrapRef.current.releasePointerCapture(ev2.pointerId); } catch (err) {}
      try { window.removeEventListener('pointermove', docMove); window.removeEventListener('pointerup', docUp); } catch (err) {}
      try {
        if (pendingRef.current != null) {
          const v = pendingRef.current; pendingRef.current = null; try { if (typeof onChange === 'function') onChange(v); } catch (err) {}
        }
      } catch (err) {}
    };

    docMoveRef.current = docMove;
    docUpRef.current = docUp;
    try { window.addEventListener('pointermove', docMove); window.addEventListener('pointerup', docUp); } catch (err) {}

    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="vertical-control-wrapper" ref={wrapRef} onPointerDown={handleWrapperPointerDown} style={{ position: 'relative', height: '100%', width: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <input
        ref={inputRef}
        className="vertical-slider overlay-thumb-hidden"
        type="range"
        min={0}
        max={1}
        step={0.001}
        value={localNorm}
        onInput={(e) => {
          const norm = parseFloat(e.target.value);
          if (isNaN(norm)) return;
          setLocalNorm(norm);
          scheduleCommit(norm);
        }}
        onPointerDown={(e) => {
          const el = inputRef.current || wrapRef.current;
          if (el && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const thumbH = 20;
            const innerTop = rect.top + thumbH / 2;
            const innerHeight = Math.max(1, rect.height - thumbH);
            let ratio = 1 - ((e.clientY - innerTop) / innerHeight);
            ratio = Math.max(0, Math.min(1, ratio));
            setLocalNorm(ratio);
            scheduleCommit(ratio);
          }
          draggingRef.current = true;
          try { if (inputRef.current.setPointerCapture) inputRef.current.setPointerCapture(e.pointerId); } catch (err) {}
          const docMove = (ev) => {
            try {
              const el2 = inputRef.current || wrapRef.current;
              if (!el2) return;
              const rect2 = inputRef.current.getBoundingClientRect();
              const thumbH2 = 20;
              const innerTop2 = rect2.top + thumbH2 / 2;
              const innerHeight2 = Math.max(1, rect2.height - thumbH2);
              let ratio2 = 1 - ((ev.clientY - innerTop2) / innerHeight2);
              ratio2 = Math.max(0, Math.min(1, ratio2));
              setLocalNorm(ratio2);
              scheduleCommit(ratio2);
            } catch (err) {}
            try { ev.preventDefault(); } catch (err) {}
          };
          const docUp = (ev) => {
            draggingRef.current = false;
            try { if (inputRef.current.releasePointerCapture && typeof ev.pointerId === 'number') inputRef.current.releasePointerCapture(ev.pointerId); } catch (err) {}
            try { window.removeEventListener('pointermove', docMove); window.removeEventListener('pointerup', docUp); } catch (err) {}
            try {
              if (pendingRef.current != null) {
                const v = pendingRef.current; pendingRef.current = null; try { if (typeof onChange === 'function') onChange(v); } catch (err) {}
              }
            } catch (err) {}
          };
          docMoveRef.current = docMove;
          docUpRef.current = docUp;
          try { window.addEventListener('pointermove', docMove); window.addEventListener('pointerup', docUp); } catch (err) {}
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          const el = inputRef.current || wrapRef.current;
          if (el && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const thumbH = 20;
            const innerTop = rect.top + thumbH / 2;
            const innerHeight = Math.max(1, rect.height - thumbH);
            let ratio = 1 - ((e.clientY - innerTop) / innerHeight);
            ratio = Math.max(0, Math.min(1, ratio));
            setLocalNorm(ratio);
            scheduleCommit(ratio);
          }
          try { e.preventDefault(); } catch (err) {}
          try { e.stopPropagation(); } catch (err) {}
        }}
        onPointerUp={(e) => {
          draggingRef.current = false;
          try { if (inputRef.current.releasePointerCapture) inputRef.current.releasePointerCapture(e.pointerId); } catch (err) {}
          try { if (docMoveRef.current) { window.removeEventListener('pointermove', docMoveRef.current); window.removeEventListener('mousemove', docMoveRef.current); } } catch (err) {}
          try { if (docUpRef.current) { window.removeEventListener('pointerup', docUpRef.current); window.removeEventListener('mouseup', docUpRef.current); } } catch (err) {}
          try {
            if (pendingRef.current != null) {
              const v = pendingRef.current; pendingRef.current = null; try { if (typeof onChange === 'function') onChange(v); } catch (err) {}
            }
          } catch (err) {}
          try { e.stopPropagation(); } catch (err) {}
        }}
        onPointerCancel={(e) => {
          draggingRef.current = false;
          try { if (inputRef.current.releasePointerCapture) inputRef.current.releasePointerCapture(e.pointerId); } catch (err) {}
          try { if (docMoveRef.current) { window.removeEventListener('pointermove', docMoveRef.current); window.removeEventListener('mousemove', docMoveRef.current); } } catch (err) {}
          try { if (docUpRef.current) { window.removeEventListener('pointerup', docUpRef.current); window.removeEventListener('mouseup', docUpRef.current); } } catch (err) {}
          try { e.stopPropagation(); } catch (err) {}
        }}
        onMouseDown={(e) => { try { e.stopPropagation(); } catch (err) {} }}
        onTouchStart={(e) => { try { e.stopPropagation(); } catch (err) {} }}
        onDoubleClick={() => { if (onDoubleClick) onDoubleClick(); }}
      />
      <div className="custom-thumb" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%) translateY(-50%)', top: topPercent + '%', width: '44px', height: '20px', borderRadius: '6px', background: 'var(--accent)', border: '2px solid var(--accent-contrast)', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }} />
      <div className="zero-line" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: zeroTopPct + '%', width: '32px', height: '2px', background: '#555', borderRadius: '2px', pointerEvents: 'none' }} />
    </div>
  );
});

export default function ChannelColumn({ prov, category, ch, idx, getEntryValue, setEntryValue, groups = [], isMaster = false, nameRef, onNamePointerDown, onNameClick }) {
  const [eqOpen, setEqOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);

  // helper to read entries; master uses category 'm' and index 0
  const read = (key) => {
    if (isMaster) {
      // allow getEntryValue to find any m.* entry when index is null
      return getEntryValue ? getEntryValue('m', null, key) : '';
    }
    return getEntryValue ? getEntryValue(category, ch.index, key) : '';
  };

  const write = useCallback((key, val) => {
    if (isMaster) return; // master channel writes are blocked in this callback (use writeEQ for EQ params)
    if (setEntryValue) setEntryValue(category, ch.index, key, val);
  }, [isMaster, setEntryValue, category, ch && ch.index]);

  // writeEQ allows writing parameters for both regular and master channels (used for EQ, Gate, and other effects)
  const writeEQ = useCallback((key, val) => {
    if (!setEntryValue) return;
    setEntryValue(isMaster ? 'm' : category, isMaster ? null : ch.index, key, val);
  }, [isMaster, setEntryValue, category, ch && ch.index]);

  // writeDyn allows writing dynamics (compressor) parameters for both regular and master channels
  const writeDyn = useCallback((key, val) => {
    if (!setEntryValue) return;
    setEntryValue(isMaster ? 'm' : category, isMaster ? null : ch.index, key, val);
  }, [isMaster, setEntryValue, category, ch && ch.index]);

  const handleGainChange = useCallback((v) => { write('gain', v); }, [write]);
  const handleMixChange = useCallback((v) => { write('mix', v); }, [write]);

  return (
    <div ref={prov ? prov.innerRef : null} {...(prov ? prov.draggableProps : {})} className={isMaster ? 'channel-column master-channel' : 'channel-column'} style={{ ...(prov ? prov.draggableProps.style : {}) }}>
      <div className="panner-wrapper">
        <input
          type="range"
          min="0"
          max="1"
          step="0.001"
          className="panner-slider"
          value={( () => { const raw = read('pan'); const num = parseFloat(raw); return isNaN(num) ? 0.5 : num; })()}
          onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) write('pan', v); }}
          onDoubleClick={() => { write('pan', 0.5); }}
        />
      </div>

      <div className="button-rows">
        <div className="button-row">
          <Button variant="secondary" size="sm">FX</Button>
          {(() => {
            const raw = read('eq.bypass');
            const on = String(raw) !== '1';
            return (
              <Button
                variant={on ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setEqOpen(true)}
                title="Open EQ settings"
              >{'EQ'}</Button>
            );
          })()}
        </div>
{!isMaster && category !== 'a' && category !== 's' ? (
        <div className="button-row single">
          {(() => {
            const raw = read('solo');
            const on = String(raw) === '1' || raw === 1;
            return (
              <Button variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => { if (!isMaster) write('solo', on ? 0 : 1); }}>{'SOLO'}</Button>
            );
          })()}
                  </div>
) : null}
{!isMaster && category !== 'a' && category !== 's' ? (
        <div className="button-row single">
          {(() => {
            const raw = read('invert');
            const on = String(raw) === '1' || raw === 1;
            return (
              <Button variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => { if (!isMaster) write('invert', on ? 0 : 1); }}>{'PHASE'}</Button>
            );
          })()}
        </div>
) : null}
                  {!isMaster && category !== 'a' && category !== 's' ? (
                  <div className="button-row single">
                    {(() => {
                      const raw = read('phantom');
                      const on = String(raw) === '1' || raw === 1;
                      return (
                        <Button variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => { if (!isMaster) write('phantom', on ? 0 : 1); }}>{'PHANTOM'}</Button>
                      );
                    })()}
                  </div>
                ) : null}
            {category === 'i' && (String(ch.index) === '0' || String(ch.index) === '1') ? (
              <div className="button-row single">
                {(() => {
                  const raw = read('hiz');
                  const on = String(raw) === '1' || raw === 1;
                  return (
                    <Button variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => { if (!isMaster) write('hiz', on ? 0 : 1); }}>{'HI-Z'}</Button>
                  );
                })()}
              </div>
            ) : null}
            {category === 'i' && (String(ch.index) === '0' || String(ch.index) === '1') ? (
              <div className="button-row single">
                {(() => {
                  const raw = read('digitech.enabled');
                  const on = String(raw) === '1' || raw === 1;
                  return (
                    <Button variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => { if (!isMaster) write('digitech.enabled', on ? 0 : 1); }}>{'DIGITECH'}</Button>
                  );
                })()}
              </div>
            ) : null}
        <div className="button-row single">
          {(() => {
            const raw = read('dyn.bypass');
            const on = String(raw) !== '1';
            return (
              <Button
                variant={on ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setCompOpen(true)}
                title="Open Compressor settings"
              >{'COMP'}</Button>
            );
          })()}
        </div>

        <div className="button-row single">
          {(() => {
            const rawEnabled = read('gate.enabled');
            const rawBypass = read('gate.bypass');
            const on = String(rawEnabled) !== '0' && String(rawBypass) !== '1';
            return (
              <Button
                variant={on ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setGateOpen(true)}
                title="Open Gate settings"
              >{'GATE'}</Button>
            );
          })()}
        </div>

        <div className="button-row single">
          {(() => {
            const raw = read('mute');
            const on = String(raw) === '1' || raw === 1;
            return (
              <Button variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => { if (!isMaster) write('mute', on ? 0 : 1); }}>{'MUTE'}</Button>
            );
          })()}
        </div>
      </div>

      <div className="slider-area">
        {category !== 'a' && category !== 's' && !isMaster && (
          <div className="control-col">
            <div className="slider-wrapper">
              <VerticalSliderOverlay
                value={( () => { const raw = read('gain'); const num = parseFloat(raw); return isNaN(num) ? 0 : num; })()}
                onChange={handleGainChange}
                onDoubleClick={() => { if (!isMaster) write('gain', 1); }}
                maxLinear={10}
              />
            </div>
            <div className="control-label">GAIN</div>
            <input
              className="gain-input"
              type="number"
              step="0.1"
              min={-120}
              max={12}
              value={( () => { const raw = read('gain'); const num = parseFloat(raw); if (isNaN(num) || num <= 0) return (-120.0).toFixed(1); const db = 20 * Math.log10(num); const rounded = Math.round(db * 10) / 10; return rounded.toFixed(1); })()}
              onChange={(e) => { const db = parseFloat(String(e.target.value).replace(',', '.')); if (isNaN(db) || !setEntryValue) return; const linear = Math.pow(10, db / 20); write('gain', linear); }}
            />
          </div>
        )}

        <div className="control-col">
          <div className="slider-wrapper">
            <VerticalSliderOverlay
              value={( () => { const raw = read('mix'); const num = parseFloat(raw); return isNaN(num) ? 0 : num; })()}
              onChange={handleMixChange}
              onDoubleClick={() => { if (!isMaster) write('mix', 1); }}
              maxLinear={10}
            />
          </div>
          {category !== 'a' && category !== 's' ? (
            <div className="control-label">MIX</div>
          ) : null}
          <input
            className="gain-input"
            type="number"
            step={0.1}
            min={-120}
            max={12}
            value={( () => { const raw = read('mix'); const num = parseFloat(raw); if (isNaN(num) || num <= 0) return (-120).toFixed(1); const db = 20 * Math.log10(num); const rounded = Math.round(db * 10) / 10; return rounded.toFixed(1); })()}
            onChange={(e) => { const db = parseFloat(String(e.target.value).replace(',', '.')); if (isNaN(db) || !setEntryValue) return; const linear = Math.pow(10, db / 20); write('mix', linear); }}
          />
        </div>
      </div>

      <div>
        <div
          ref={nameRef}
          className="channel-name-btn"
          role="button"
          tabIndex={0}
          {...(prov ? prov.dragHandleProps : {})}
          onPointerDown={onNamePointerDown}
          onClick={onNameClick}
          style={{ cursor: 'inherit', userSelect: 'none', textAlign: 'center' }}
        >
          {ch.name || (isMaster ? 'MASTER' : '')}
        </div>
      </div>

      {isMaster ? (
        <MasterCompressorDialog
          open={compOpen}
          onClose={() => setCompOpen(false)}
          read={read}
          write={writeDyn}
        />
      ) : (
        <CompressorDialog
          open={compOpen}
          onClose={() => setCompOpen(false)}
          read={read}
          write={write}
          channelName={ch.name || ''}
        />
      )}

      <GateDialog
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        read={read}
        write={writeEQ}
        channelName={ch.name || ''}
      />
      {isMaster ? (
        <MasterEQDialog
          open={eqOpen}
          onClose={() => setEqOpen(false)}
          read={read}
          write={writeEQ}
        />
      ) : (
        <EQDialog
          open={eqOpen}
          onClose={() => setEqOpen(false)}
          read={read}
          write={writeEQ}
          channelName={ch.name || ''}
        />
      )}
    </div>
  );
}
